// URL-path document routing. One document per load means zero
// client-side navigation: the shell parses `/{type}/{id}` from
// `location.pathname` once at boot and switches on it — no router library.

export const DOCUMENT_TYPES = ["research", "transcript", "filing"] as const;

export type DocumentType = (typeof DOCUMENT_TYPES)[number];

export interface DocumentRoute {
  type: DocumentType;
  id: string;
}

function isDocumentType(value: string): value is DocumentType {
  return (DOCUMENT_TYPES as readonly string[]).includes(value);
}

function safeDecode(segment: string): string | null {
  try {
    return decodeURIComponent(segment);
  } catch {
    return null; // malformed percent-encoding
  }
}

/**
 * Parse `/{type}/{id}` from a pathname. Returns null for unknown types,
 * missing ids, or extra segments — the shell renders the unknown-document
 * error state for those.
 */
export function parseDocumentPath(pathname: string): DocumentRoute | null {
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length !== 2) return null;

  const type = safeDecode(segments[0]);
  const id = safeDecode(segments[1]);

  if (type === null || id === null || !isDocumentType(type) || !id) {
    return null;
  }
  return { type, id };
}
